/**
 * Public /programmes/check-suitability/:id — red / amber / green screening.
 *
 * Flow:
 *   1. Fetch questionnaire schema from `GET /api/programmes/screening/:id`
 *   2. Patient answers → `POST /api/programmes/screening`
 *   3. Response outcome decides what we show:
 *      • red   → block + escalation copy (999 / A&E / NHS 111 depending on flag)
 *      • amber → programme locked, offer clinician-assessment CTA
 *      • green → `token` unlocks the "Continue to checkout" button which
 *                 forwards to /checkout/summary?package=<id>&plan=full&screen=<token>
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, Loader2, ShieldAlert } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function ProgrammeScreening() {
  const { id: programmeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [country, setCountry] = useState("United Kingdom");
  const [activityGoal, setActivityGoal] = useState("");
  // Guest fields — required when the caller isn't signed in so the screening
  // token can be bound to their email and the account can be provisioned
  // after Stripe confirms payment.
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/online-programmes/screening/${programmeId}`)
      .then((r) => setSchema(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Programme not found"))
      .finally(() => setLoading(false));
  }, [programmeId]);

  const questions = schema?.questions || [];
  const answeredAll = useMemo(
    () => questions.every((q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== ""),
    [questions, answers],
  );

  const onAnswer = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const submit = async () => {
    if (!user) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());
      if (!emailOk || !guestName.trim()) {
        setError("Please enter your full name and email so we can continue as a guest.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post("/online-programmes/screening", {
        programme_id: programmeId, answers, country, activity_goal: activityGoal,
        guest_email: user ? undefined : guestEmail.trim(),
        guest_name:  user ? undefined : guestName.trim(),
      });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Screening failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const continueToCheckout = () => {
    if (!result?.token) return;
    const qs = new URLSearchParams({
      package: programmeId, plan: "full", screen: result.token,
    });
    // For guest flow, pre-fill the checkout so the buyer doesn't retype
    // details they already gave us during screening.
    if (!user) {
      qs.set("email", guestEmail.trim());
      qs.set("name", guestName.trim());
    }
    navigate(`/checkout/summary?${qs.toString()}`);
  };

  return (
    <div className="fl-page min-h-screen" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />
      <section className="pt-32 md:pt-40 pb-10 px-6 bg-[#0A0A0A] fl-noise">
        <div className="max-w-3xl mx-auto">
          <Link to="/prices#online-programmes" className="text-xs text-[color:var(--fl-orange)] hover:underline" data-testid="screening-back-to-prices">
            ← Back to online programmes
          </Link>
          <div className="fl-label text-[color:var(--fl-orange)] mt-4">Check suitability</div>
          <h1 className="fl-heading text-4xl md:text-5xl mt-3" data-testid="screening-heading">
            {schema?.programme?.name || "Online programme"}
          </h1>
          <p className="text-sm mt-3 max-w-xl" style={{ color: "var(--fl-muted)" }}>
            This short questionnaire (~2 min) is a standardised safety check — not a diagnosis.
            We ask a few red-flag and history questions so we know a self-guided programme is
            appropriate for you before you pay.
          </p>
        </div>
      </section>

      <section className="py-10 px-6 bg-[#121212] border-t border-white/5 min-h-[400px]">
        <div className="max-w-3xl mx-auto">
          {loading && (
            <div className="fl-card p-8 text-center text-sm text-zinc-400" data-testid="screening-loading">
              <Loader2 className="animate-spin mx-auto mb-2" /> Loading questionnaire…
            </div>
          )}
          {error && !result && (
            <div className="fl-card p-6 text-sm text-red-300 border border-red-500/40" data-testid="screening-error">{error}</div>
          )}

          {result?.outcome === "red" && <RedOutcome result={result} programmeId={programmeId} />}
          {result?.outcome === "amber" && <AmberOutcome result={result} programmeId={programmeId} />}
          {result?.outcome === "green" && (
            <GreenOutcome programme={schema?.programme} onContinue={continueToCheckout} />
          )}

          {!result && !loading && schema && (
            <div className="fl-card p-6 md:p-8" data-testid="screening-form">
              <div className="grid gap-4 mb-6">
                {!user && (
                  <div className="grid md:grid-cols-2 gap-3 p-4 rounded-md" style={{ background: "color-mix(in oklab, var(--fl-orange) 6%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 25%, transparent)" }}>
                    <label className="text-xs md:col-span-2 fl-label text-[color:var(--fl-orange)]">You're continuing as a guest — tell us who you are so we can send you the programme after payment.</label>
                    <input
                      className="fl-input" placeholder="Full name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      data-testid="screening-guest-name"
                    />
                    <input
                      className="fl-input" type="email" placeholder="Email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      data-testid="screening-guest-email"
                    />
                  </div>
                )}
                <label className="text-xs">
                  <div className="fl-label text-[color:var(--fl-orange)] mb-1">Current country</div>
                  <input
                    className="fl-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="screening-country"
                  />
                </label>
                <label className="text-xs">
                  <div className="fl-label text-[color:var(--fl-orange)] mb-1">What activity are you working towards?</div>
                  <input
                    className="fl-input"
                    placeholder="e.g. running 5K, gym pressing, lifting my kids"
                    value={activityGoal}
                    onChange={(e) => setActivityGoal(e.target.value)}
                    data-testid="screening-goal"
                  />
                </label>
              </div>

              <div className="space-y-5">
                {questions.map((q, idx) => (
                  <QuestionRow
                    key={q.id}
                    idx={idx + 1}
                    q={q}
                    value={answers[q.id]}
                    onChange={(v) => onAnswer(q.id, v)}
                  />
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap border-t border-white/5 pt-6">
                <div className="text-[11px] text-zinc-500 max-w-md">
                  We store your answers, questionnaire version and outcome so our clinicians can
                  audit and review your care — nothing more. See our{" "}
                  <Link to="/privacy" className="text-[color:var(--fl-orange)] hover:underline">Privacy Policy</Link>.
                </div>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!answeredAll || submitting}
                  className="fl-btn text-sm justify-center"
                  data-testid="screening-submit"
                >
                  {submitting ? (<><Loader2 size={14} className="animate-spin" /> Checking…</>)
                              : (<>Check my eligibility <ArrowRight size={14} /></>)}
                </button>
              </div>
              {!answeredAll && (
                <div className="text-[11px] text-amber-300/80 mt-3 text-right">Please answer every question to continue.</div>
              )}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function QuestionRow({ idx, q, value, onChange }) {
  return (
    <div className="fl-card p-4 md:p-5" data-testid={`screening-q-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="fl-label text-[color:var(--fl-orange)] shrink-0 mt-1">{String(idx).padStart(2, "0")}</div>
        <div className="flex-1">
          <div className="text-sm leading-snug">{q.label}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {q.kind === "yes_no" && (
              <>
                <Choice active={value === "yes"} onClick={() => onChange("yes")} testid={`screening-q-${q.id}-yes`}>Yes</Choice>
                <Choice active={value === "no"} onClick={() => onChange("no")} testid={`screening-q-${q.id}-no`}>No</Choice>
              </>
            )}
            {q.kind === "single" && (q.choices || []).map((c) => (
              <Choice key={c.value} active={value === c.value} onClick={() => onChange(c.value)} testid={`screening-q-${q.id}-${c.value}`}>
                {c.label}
              </Choice>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Choice({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`fl-btn-anim text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-[#f47a20] text-white border-[#f47a20]"
               : "bg-transparent hover:border-[#f47a20]/60 text-zinc-300"
      }`}
      style={!active ? { borderColor: "var(--fl-border)" } : {}}
      data-testid={testid}
    >{children}</button>
  );
}

function RedOutcome({ result, programmeId }) {
  const first = result.flags?.find((f) => f.severity === "red");
  return (
    <div className="fl-card p-6 md:p-8 border-l-4 border-l-red-500 bg-red-500/[0.06]" data-testid="screening-outcome-red">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="text-red-400" size={24} />
        <div className="fl-heading text-2xl text-red-300">Please seek urgent care</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--fl-text-2)" }}>
        Based on your answers we can't safely offer you a self-guided programme.
        Your responses suggest symptoms that need urgent medical review — please act now.
      </p>
      {first?.message && (
        <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-200 mb-4">
          {first.message}
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-2 text-sm mt-4">
        <a href="tel:999" className="fl-btn bg-red-600 hover:bg-red-500 justify-center" data-testid="screening-call-999">Call 999</a>
        <a href="tel:111" className="fl-btn-ghost justify-center" data-testid="screening-call-111">Call NHS 111</a>
        <Link to="/contact" className="fl-btn-ghost justify-center" data-testid="screening-contact-us">Talk to us</Link>
      </div>
      <div className="text-[11px] text-zinc-500 mt-6">Screening ID logged for governance · programme: {programmeId}</div>
    </div>
  );
}

function AmberOutcome({ result, programmeId }) {
  const first = result.flags?.[0];
  return (
    <div className="fl-card p-6 md:p-8 border-l-4 border-l-amber-500 bg-amber-500/[0.06]" data-testid="screening-outcome-amber">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="text-amber-400" size={24} />
        <div className="fl-heading text-2xl text-amber-300">Programme locked — clinician assessment recommended</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--fl-text-2)" }}>
        Based on your answers a self-guided programme isn't the safest first step for you.
        We'd like you to book a live assessment so we can build a plan that's safe and effective.
      </p>
      {first?.message && (
        <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200 mb-4">
          {first.message}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2 mt-4">
        <Link to="/app/appointments?service=Initial+Assessment" className="fl-btn justify-center" data-testid="screening-book-assessment">
          Book clinician assessment <ArrowRight size={14} />
        </Link>
        <Link to="/contact" className="fl-btn-ghost justify-center" data-testid="screening-amber-contact">Talk to us instead</Link>
      </div>
      <div className="text-[11px] text-zinc-500 mt-6">Screening ID logged for governance · programme: {programmeId}</div>
    </div>
  );
}

function GreenOutcome({ programme, onContinue }) {
  return (
    <div className="fl-card p-6 md:p-8 border-l-4 border-l-emerald-500 bg-emerald-500/[0.06]" data-testid="screening-outcome-green">
      <div className="flex items-center gap-2 mb-3">
        <Check className="text-emerald-400" size={24} />
        <div className="fl-heading text-2xl text-emerald-300">You're good to go</div>
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--fl-text-2)" }}>
        Your answers look consistent with a self-guided programme. Continue to checkout to
        unlock {programme?.name || "the programme"} — you'll receive the PDF by email and it
        will also be available from your patient portal.
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="fl-btn text-base justify-center w-full sm:w-auto"
        data-testid="screening-continue-checkout"
      >
        Continue to checkout <ArrowRight size={16} />
      </button>
    </div>
  );
}
